import { FunctionComponent, useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FolderIcon from '@mui/icons-material/Folder';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import { savedApi, ApiError } from '../../../services/api';
import styles from './SaveToModal.module.css';

interface SaveToModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    type: 'provider' | 'medication' | 'procedure' | 'care-plan';
    title: string;
    subtitle?: string;
    data: any;
  };
  onCreateNewList?: () => void;
}

const SaveToModal: FunctionComponent<SaveToModalProps> = ({
  isOpen,
  onClose,
  item,
  onCreateNewList
}) => {
  const { savedItems, saveItem, removeItem, isItemSaved } = useSavedItems();
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [isSavedToPage, setIsSavedToPage] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Get all saved lists
  const savedLists = savedItems.filter(savedItem => savedItem.type === 'list');
  
  useEffect(() => {
    if (isOpen) {
      // Check if item is already saved to the main saved page
      setIsSavedToPage(isItemSaved(item.id, item.type));
      
      // Check which lists already contain this item
      const listsWithItem = savedLists.filter(list => {
        const categories = list.data?.categories;
        if (!categories) return false;
        
        switch (item.type) {
          case 'provider':
            return categories.providers?.some((p: any) => p.id === item.id);
          case 'medication':
            return categories.medications?.some((m: any) => m.id === item.id);
          case 'procedure':
            return categories.procedures?.some((p: any) => p.id === item.id);
          default:
            return false;
        }
      }).map(list => list.id);
      
      setSelectedLists(listsWithItem);
      setShowSuccess(false);
    }
  }, [isOpen, item, savedLists]);

  const handleSaveToPage = () => {
    if (isSavedToPage) {
      removeItem(item.id, item.type);
      setIsSavedToPage(false);
    } else {
      saveItem({
        id: item.id,
        type: item.type,
        title: item.title,
        subtitle: item.subtitle,
        savedAt: new Date(),
        data: item.data
      });
      setIsSavedToPage(true);
    }
    showSuccessMessage();
  };

  const handleToggleList = async (listId: string) => {
    const list = savedLists.find(l => l.id === listId);
    if (!list) return;

    const isInList = selectedLists.includes(listId);
    
    try {
      // Fetch the full list data from API to ensure we have the latest categories
      const listData = await savedApi.getListById(listId);
      
      let updatedCategories = { ...listData.categories };

      if (isInList) {
        // Remove from list
        switch (item.type) {
          case 'provider':
            updatedCategories.providers = updatedCategories.providers.filter((p: any) => p.id !== item.id);
            break;
          case 'medication':
            updatedCategories.medications = updatedCategories.medications.filter((m: any) => m.id !== item.id);
            break;
          case 'procedure':
            updatedCategories.procedures = updatedCategories.procedures.filter((p: any) => p.id !== item.id);
            break;
        }
        setSelectedLists(selectedLists.filter(id => id !== listId));
      } else {
        // Add to list
        switch (item.type) {
          case 'provider':
            updatedCategories.providers = [...(updatedCategories.providers || []), item.data];
            break;
          case 'medication':
            updatedCategories.medications = [...(updatedCategories.medications || []), item.data];
            break;
          case 'procedure':
            updatedCategories.procedures = [...(updatedCategories.procedures || []), item.data];
            break;
        }
        setSelectedLists([...selectedLists, listId]);
      }

      // Update the list via API
      await savedApi.updateList(listId, {
        ...listData,
        categories: updatedCategories
      });

      // Update local state
      const updatedList = {
        ...list,
        data: {
          ...listData,
          categories: updatedCategories
        }
      };
      
      // Update the saved list in context
      removeItem(listId, 'list');
      saveItem(updatedList);
      
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating list:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
      // Revert selection state on error
      if (isInList) {
        setSelectedLists([...selectedLists, listId]);
      } else {
        setSelectedLists(selectedLists.filter(id => id !== listId));
      }
    }
  };

  const showSuccessMessage = () => {
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 1500);
  };

  const getItemIcon = () => {
    switch (item.type) {
      case 'provider':
        return <PersonIcon className={styles.itemIcon} />;
      case 'medication':
        return <MedicationIcon className={styles.itemIcon} />;
      case 'procedure':
        return <MedicalServicesIcon className={styles.itemIcon} />;
      case 'care-plan':
        return <AutoAwesomeIcon className={styles.itemIcon} />;
      default:
        return null;
    }
  };

  const getItemCount = (list: any) => {
    // Handle both local saved lists and API fetched lists
    const categories = list.data?.categories;
    if (!categories) {
      // If categories is not available, try to get itemCount from data
      return list.data?.itemCount || 0;
    }
    return (categories.providers?.length || 0) + 
           (categories.medications?.length || 0) + 
           (categories.procedures?.length || 0) +
           (categories.articles?.length || 0);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Save to...</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          {showSuccess ? (
            <div className={styles.successMessage}>
              <CheckIcon className={styles.successIcon} />
              <p>Saved successfully!</p>
            </div>
          ) : (
            <>
              <div className={styles.itemPreview}>
                <div className={styles.itemIconContainer}>
                  {getItemIcon()}
                </div>
                <div className={styles.itemInfo}>
                  <h3 className={styles.itemTitle}>{item.title}</h3>
                  {item.subtitle && (
                    <p className={styles.itemSubtitle}>{item.subtitle}</p>
                  )}
                </div>
              </div>

              <div className={styles.saveOptions}>
                <button
                  className={`${styles.saveOption} ${isSavedToPage ? styles.selected : ''}`}
                  onClick={handleSaveToPage}
                >
                  <div className={styles.optionContent}>
                    <BookmarkIcon className={styles.optionIcon} />
                    <div className={styles.optionText}>
                      <h4>My Saved Items</h4>
                      <p>Quick access from your saved page</p>
                    </div>
                  </div>
                  <div className={styles.checkbox}>
                    {isSavedToPage && <CheckIcon />}
                  </div>
                </button>

                <div className={styles.listsSection}>
                  <div className={styles.listsSectionHeader}>
                    <h3>Save to Lists</h3>
                    <button
                      className={styles.createListButton}
                      onClick={() => {
                        onClose();
                        onCreateNewList?.();
                      }}
                    >
                      <AddIcon />
                      New List
                    </button>
                  </div>

                  {savedLists.length === 0 ? (
                    <div className={styles.emptyLists}>
                      <p>No lists yet</p>
                      <button
                        className={styles.createFirstListButton}
                        onClick={() => {
                          onClose();
                          onCreateNewList?.();
                        }}
                      >
                        Create your first list
                      </button>
                    </div>
                  ) : (
                    <div className={styles.listOptions}>
                      {savedLists.map((list) => (
                        <button
                          key={list.id}
                          className={`${styles.listOption} ${selectedLists.includes(list.id) ? styles.selected : ''}`}
                          onClick={() => handleToggleList(list.id)}
                        >
                          <div className={styles.listContent}>
                            <FolderIcon className={styles.listIcon} />
                            <div className={styles.listText}>
                              <h4>{list.title}</h4>
                              <p>{getItemCount(list)} items</p>
                            </div>
                          </div>
                          <div className={styles.checkbox}>
                            {selectedLists.includes(list.id) && <CheckIcon />}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveToModal;